import ActionMenu from "Components/Common/ActionMenu";
import TableContainer from "Components/Common/TableContainer";
import CustomButton from "Components/Custom/CustomButton";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { useDebounce } from "Components/Hooks/useDebounce";
import useItemCalculation from "Components/Hooks/useItemCalculation";
import Icon from "Components/Icons/Icon";
import CheckMarginModal from "Components/Modals/CheckMarginModal";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import PaymentMethodModal from "Components/Modals/PaymentMethodModal";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import Stepper from "Components/presetQuotes/Stepper";
import ItemsFilter from "Components/QuoteAndInvoice/ItemsFilter";
import AddCategoryModal from "Components/Quotes/ManageQuotes/AddCategoryModal";
import AddSubCategoryModal from "Components/Quotes/ManageQuotes/AddSubCategoryModal";
import DiscountModal from "Components/Quotes/ManageQuotes/DiscountModal";
import ManageItemModal from "Components/Quotes/ManageQuotes/ManageItemModal";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Col } from "reactstrap";
import { createSelector } from "reselect";
import { resetEditInvoiceStatus } from "slices/invoices/reducer";
import {
  findAndReplaceAddedItem,
  selectItemsState,
  setCalculatedTotalItems,
} from "slices/items/reducer";
import {
  deleteItem,
  editInvoice,
  getCategories,
  getItems,
} from "slices/thunks";
import { ItemData } from "types/item.api";
import { ErrorHandler, ErrorToast } from "utils/toast";
import { calculateMarginData } from "utils/utils";

interface props {
  onSubmit: () => void;
}

const ManageInvoiceItem = ({ onSubmit }: props) => {
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const invoiceDetails = useAppSelector(
    (state) => state.invoice.invoiceDetails,
  );

  const user = useAppSelector((state) => state.auth.user);
  const [quantity, setQuantity] = useState<number>(0);
  const [showManageItemModal, setShowManageItemModal] =
    useState<boolean>(false);
  const [openMarginModal, setOpenMarginModal] = useState<boolean>(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] =
    useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "stripe" | null>(
    invoiceDetails?.payment_method || user?.stripe_account_status === "active"
      ? "stripe"
      : "cash",
  );
  const [isShowStripeConnectModal, setIsShowStripeConnectModal] =
    useState(false);

  const addItems = useAppSelector((state) => state.item.addItems);
  const [searchCategoryText] = useState("");
  const { categories } = useAppSelector((stat) => stat.category);
  const { discount, setDiscount, totals } = useItemCalculation(addItems);

  // const creditApplied = Math.min(
  //   invoiceDetails?.client?.available_credit || 0,
  //   totals.grandTotal,
  // );
  // const grandTotalAfterCredit = Math.max(totals.grandTotal - creditApplied, 0);

  const debounceCategorySearch = useDebounce(searchCategoryText, 500);
  const initialCall = useRef(true);
  // Memoize all toggle functions with use
  useEffect(() => {
    if (!initialCall.current) {
      dispatch(getCategories({ search: searchCategoryText }));
    } else {
      initialCall.current = false;
    }
  }, [debounceCategorySearch]);

  useEffect(() => {
    if (invoiceDetails) {
      setDiscount(Number(invoiceDetails.quote?.discount?.amount ?? 0));
    }
  }, [invoiceDetails]);

  useEffect(() => {
    dispatch(
      setCalculatedTotalItems({
        subTotal: totals.subTotal,
        totalTax: totals.taxAmount,
        totalDiscount: totals.discountAmount,
        grandTotal: totals.grandTotal,
        totalDeposit: 0,
        depositType: null,
      }),
    );
  }, [totals, dispatch]);

  useEffect(() => {
    if (invoiceDetails) {
      setPaymentMethod(
        invoiceDetails?.payment_method
          ? invoiceDetails?.payment_method
          : user?.stripe_account_status === "active"
          ? "stripe"
          : "cash",
      );
    }
  }, [user, invoiceDetails]);

  const itemsSelector = createSelector(selectItemsState, (state) => ({
    items: state.items,
    isLoading: state.status.fetch,
    meta: state.pagination,
  }));
  const [searchText, setSearchText] = useState("");
  const { items, meta, isLoading } = useAppSelector(itemsSelector);
  const statusOfEditInvoice = useAppSelector(
    (state) => state.invoice.status.edit,
  );

  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [selectedSubcategory, setSelectedSubcategory] = useState<number[]>([]);

  const toggleMarginModal = useCallback(() => {
    setOpenMarginModal((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!invoiceDetails) return;
    const params = searchText ? { search: searchText } : { page: currentPage };
    dispatch(
      getItems({
        ...params,
        invoice_id: invoiceDetails.id,
        ...(selectedCategory ? { category_id: Number(selectedCategory) } : {}),
        ...(selectedSubcategory.length > 0
          ? { subcategory_ids: selectedSubcategory }
          : {}),
      }),
    );
  }, [
    currentPage,
    dispatch,
    selectedCategory,
    searchText,
    selectedSubcategory,
    invoiceDetails,
  ]);

  const handleQuantityChange = useCallback(
    (item: ItemData, quantity: number) => {
      dispatch(findAndReplaceAddedItem({ ...item, quantity }));
      setQuantity(quantity);
    },
    [dispatch],
  );

  const handleSearch = useCallback((e: string) => {
    setSearchText(e);
  }, []);

  const [showItemFilter, setShowItemFilter] = useState<boolean>(false);

  const handleSave = async () => {
    if (invoiceDetails) {
      console.log("addItems", addItems);
      if (addItems.length === 0) {
        ErrorToast("Please select item to proceed further.");
        return;
      }
      try {
        await dispatch(
          editInvoice({
            invoice_id: invoiceDetails.id,
            items: addItems,
            // vat_setting_id: tax,
            discount: discount,
            payment_method: paymentMethod,
          }),
        )?.unwrap();
        dispatch(resetEditInvoiceStatus());
        onSubmit?.();
      } catch (error) {
        ErrorHandler(error);
      }
    }
  };

  const handleEditItem = useCallback(
    (rowData: ItemData) => {
      setSelectedItem({ ...rowData, quantity });
      setIsEditItemModal(true);
      setShowManageItemModal(true);
    },
    [quantity],
  );
  const [deleteModal, setDeleteModal] = useState<number | null>(null);
  const handleDeleteItem = useCallback((rowData: ItemData) => {
    setDeleteModal(rowData.id);
  }, []);

  const itemsColumn = useMemo(
    () => [
      {
        header: "ITEM NAME",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {cell.getValue()}
            </span>
          );
        },
      },
      {
        header: "CATEGORY",
        accessorKey: "category_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {cell.getValue() ? cell.getValue() : "-"}
            </span>
          );
        },
      },
      {
        header: "SUBCATEGORY",
        accessorKey: "subcategory_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {cell.getValue() ? cell.getValue() : "-"}
            </span>
          );
        },
      },
      {
        header: "QTY",
        accessorKey: "quantity",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {cell.getValue()}
            </span>
          );
        },
      },
      {
        header: "PRICE/UNIT",
        accessorKey: "price",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              £{cell.getValue().toFixed(2)}
            </span>
          );
        },
      },
      {
        header: "REMAINING QTY",
        accessorKey: "remainingQuantity",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          const foundItem = addItems.find((item) => item.id === rowData.id);
          let currentQuantity = 0;
          if (foundItem) {
            currentQuantity = foundItem.quantity;
          }

          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              <Stepper
                initialQuantity={currentQuantity}
                key={`${rowData.id}-${currentQuantity}`}
                onQuantityChange={(quantity) =>
                  handleQuantityChange(rowData, quantity)
                }
              />
            </span>
          );
        },
      },
      {
        header: "TOTAL",
        accessorKey: "total",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          const foundItem = addItems.find((item) => item.id === rowData.id);
          let currentQuantity = 0;
          if (foundItem) {
            currentQuantity = foundItem.quantity;
          }
          const total = Number(currentQuantity) * Number(rowData.price);

          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              £{total.toFixed()}
            </span>
          );
        },
      },
      {
        header: "ACTIONS",

        cell: (cell: any) => {
          return (
            <ActionMenu
              onEdit={() => handleEditItem(cell.row.original)}
              onDelete={() => handleDeleteItem(cell.row.original)}
            />
          );
        },
      },
    ],
    [addItems, handleDeleteItem, handleEditItem, handleQuantityChange],
  );

  const [openDiscountModal, setOpenDiscountModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<ItemData | null>(null);
  const toggleDiscountModal = useCallback(() => {
    setOpenDiscountModal((prev) => !prev);
  }, []);
  const [isEditItemModal, setIsEditItemModal] = useState<boolean>(false);
  const [showAddSubCategoryModal, setShowAddSubCategoryModal] =
    useState<boolean>(false);

  const [showAddCategoryModal, setShowAddCategoryModal] =
    useState<boolean>(false);

  const toggleManageItemModal = useCallback(() => {
    setSelectedItem(null);
    setIsEditItemModal(false);
    setShowManageItemModal((prev) => !prev);
  }, []);

  const toggleAddCategoryModal = useCallback(() => {
    setShowAddCategoryModal((prev) => !prev);
  }, []);

  const toggleAddSubCategoryModal = useCallback(() => {
    setShowAddSubCategoryModal((prev) => !prev);
  }, []);

  const togglePaymentMethodModal = useCallback(() => {
    setShowPaymentMethodModal((prev) => !prev);
  }, []);

  const handleSavePaymentMethod = (
    selectedMethod: "cash" | "stripe" | null,
  ) => {
    if (
      selectedMethod === "stripe" &&
      user?.stripe_account_status !== "active"
    ) {
      setShowPaymentMethodModal(false);
      setIsShowStripeConnectModal(true);
      return;
    } else {
      setPaymentMethod(selectedMethod);
      setShowPaymentMethodModal(false);
    }
  };

  useEffect(() => {
    const isAnyModalOpen =
      showManageItemModal ||
      openMarginModal ||
      showItemFilter ||
      deleteModal !== null ||
      isEditItemModal ||
      showAddCategoryModal ||
      showAddSubCategoryModal ||
      showPaymentMethodModal;
    if (isAnyModalOpen) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    }

    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [
    deleteModal,
    isEditItemModal,
    openMarginModal,
    showAddCategoryModal,
    showAddSubCategoryModal,
    showItemFilter,
    showManageItemModal,
    showPaymentMethodModal,
  ]);

  return (
    <>
      <div className="mt-2">
        <TableContainer
          columns={itemsColumn}
          data={items}
          isGlobalFilter={true}
          customPageSize={meta?.per_page}
          divClass="table-responsive table-card mb-2 bg-white"
          tableClass="align-middle table-nowrap table-borderless"
          thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table "
          theadClass="table-light"
          SearchPlaceholder="Search items"
          searchAlign="left"
          isQuoteFilter={true}
          productCatyegory={(categories || []).map((cat) => ({
            label: cat.name,
            value: cat.id,
          }))}
          onItemFilterClick={() => {
            setShowItemFilter(true);
          }}
          currentPage={currentPage}
          totalPages={meta?.last_page}
          toPage={meta?.to}
          fromPage={meta?.from}
          totalItems={meta?.total}
          onPageChange={setCurrentPage}
          onSearch={handleSearch}
          onAddItemClick={() => {
            setShowManageItemModal(true);
          }}
          isInitialLoading={isLoading === "loading" && !items?.length}
          isPaginationLoading={isLoading === "loading" && items?.length > 0}
        />
        <Col lg={12}>
          <div className="d-flex align-items-start justify-content-between w-100 pt-4 mt-4 border-top border-top border-top-dashed">
            <CustomButton
              className="border-7 bg-primary"
              text="Finalise Invoice"
              onClick={handleSave}
              loading={statusOfEditInvoice === "loading"}
            />

            <td colSpan={6} className="fw-medium p-0">
              <table className="table table-borderless mb-0">
                <tbody>
                  <tr className="d-flex justify-content-between gap-5">
                    <td className="text-grey89 fw-normal fs-14 font-primary">
                      Sub Total
                    </td>
                    <td className="text-end fw-normal fs-14 font-primary text-black2D">
                      £{totals?.subTotal.toFixed(2)}
                    </td>
                  </tr>
                  {/* <tr className="d-flex justify-content-between gap-5">
                    <td className="text-grey89 fw-normal fs-14 font-primary">
                      Credit Applied
                    </td>
                    <td className="text-end fw-normal fs-14 font-primary text-green">
                      -£{creditApplied.toFixed(2)}
                    </td>
                  </tr> */}
                  <tr className="d-flex justify-content-between gap-5">
                    <td className="text-grey89 fw-normal  fs-14 font-primary">
                      Margin (
                      {calculateMarginData(
                        addItems || [],
                      ).totals.totalMarginPercent.toFixed(2)}
                      %)
                    </td>
                    <td className="text-end">
                      <p
                        className="text-decoration-underline text-primary fw-normal fs-14 font-primary cursor-pointer mb-0 pb-0"
                        onClick={() => {
                          setOpenMarginModal(true);
                        }}
                      >
                        Check Margin
                      </p>
                    </td>
                  </tr>
                  <tr className="d-flex justify-content-between gap-5">
                    <td className="text-grey89 fw-normal  fs-14 font-primary">
                      Tax ({totals?.taxPercentage}%)
                    </td>

                    <td className="text-end fw-normal fs-14 font-primary text-black2D">
                      £{totals?.taxAmount.toFixed(2)}
                    </td>
                  </tr>
                  <tr className="d-flex justify-content-between gap-5">
                    <td className="text-grey89 fw-normal  fs-14 font-primary">
                      Discount ({discount + "%"})
                    </td>
                    <td className="text-end">
                      <p
                        className="text-primary cursor-pointer mb-0 pb-0 fw-normal fs-14 font-primary"
                        onClick={toggleDiscountModal}
                      >
                        <Icon name="Edit" />£{totals?.discountAmount.toFixed(2)}
                      </p>
                    </td>
                  </tr>
                  {/* Divider */}
                  <div className="my-2 dashed" />
                  <tr className="d-flex justify-content-between gap-5">
                    <th
                      scope="row"
                      className="text-grey89 fw-normal  fs-14 font-primary"
                    >
                      Grand Total
                    </th>
                    <th className="text-end fw-medium fs-14 font-primary text-black2D">
                      £{totals?.grandTotal}
                    </th>
                  </tr>
                  {/* Divider */}
                  <div className="my-2 dashed" />

                  <tr className="d-flex justify-content-between gap-5">
                    <td className="text-grey89 fw-normal  fs-14 font-primary">
                      Payment Method
                    </td>
                    <td className="text-end">
                      {paymentMethod ? (
                        <p
                          className="text-grey89 fw-normal fs-14 font-primary cursor-pointer mb-0 pb-0"
                          onClick={togglePaymentMethodModal}
                        >
                          <Icon name="Edit" />
                          {paymentMethod.charAt(0).toUpperCase() +
                            paymentMethod.slice(1)}
                        </p>
                      ) : (
                        <p
                          className="text-primary fw-normal fs-14 font-primary cursor-pointer mb-0 pb-0"
                          onClick={togglePaymentMethodModal}
                        >
                          + Add
                        </p>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </div>
        </Col>
      </div>
      <ItemsFilter
        onCloseClick={() => {
          setShowItemFilter(false);
        }}
        show={showItemFilter}
        onFilterChange={(selectedCategory, selectedSubcategory) => {
          setSelectedCategory(selectedCategory || undefined);
          setSelectedSubcategory(selectedSubcategory);
          setShowItemFilter(false);
        }}
      />

      <DiscountModal
        openDiscountModal={openDiscountModal}
        toggleDiscountModal={toggleDiscountModal}
        discountPercentage={discount}
        addDiscount={(discount: number) => {
          setDiscount(discount);
          toggleDiscountModal();
        }}
      />

      <ManageItemModal
        showModal={showManageItemModal}
        toggle={toggleManageItemModal}
        isEdit={isEditItemModal}
        onAddCategoryClick={toggleAddCategoryModal}
        onAddSubCategoryClick={toggleAddSubCategoryModal}
        selectedItem={selectedItem}
        quoteDetails={null}
        invoiceDetails={invoiceDetails}
      />

      <DeleteItemModal
        show={deleteModal !== null}
        onCloseClick={() => setDeleteModal(null)}
        onDeleteClick={() => {
          if (deleteModal) {
            const itemId = deleteModal;
            dispatch(deleteItem(itemId));
          }
          setDeleteModal(null);
        }}
      />

      <AddCategoryModal
        show={showAddCategoryModal}
        onCloseClick={toggleAddCategoryModal}
      />

      <AddSubCategoryModal
        show={showAddSubCategoryModal}
        onCloseClick={toggleAddSubCategoryModal}
      />

      <CheckMarginModal
        isOpen={openMarginModal}
        onClose={toggleMarginModal}
        items={addItems}
      />

      <PaymentMethodModal
        isOpen={showPaymentMethodModal}
        onClose={togglePaymentMethodModal}
        onSave={handleSavePaymentMethod}
        currentPaymentMethod={paymentMethod}
      />
      <RestrictedAccessModal
        isOpen={isShowStripeConnectModal}
        onCancel={() => setIsShowStripeConnectModal(false)}
        title="Payments & Processing Fees"
        description={`Online payments are securely processed through Stripe.
         
         A 0.5% platform processing fee applies to all card and online payments processed through Stripe, including deposit payments. Standard Stripe transaction fees may also apply separately.
         
         These fees are deducted internally from your payout, your customers will always pay the exact invoice or deposit amount shown to them.
         
         Platform fees do not apply to cash payments, bank transfers, or offline payments.`}
        buttonText="Connect Stripe"
        onButtonClick={() => {
          setIsShowStripeConnectModal(false);
          navigate("/settings", { state: { focusStripe: true } });
        }}
        bodyClassName="text-start align-content-start"
        cancelText="Not Now"
      />
    </>
  );
};

export default ManageInvoiceItem;
